import React from 'react';
import { ArrowRight, Phone } from 'lucide-react';
import { Button } from './ui/button';

const Hero = ({ t }) => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-black">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-black/20 to-transparent z-10" />
        <div className="absolute inset-0 luxury-pattern opacity-30 z-10" />
        <img 
          src="https://customer-assets.emergentagent.com/job_realtyhub-19/artifacts/n3d0nlld_Foto-2.png"
          alt="Miguel Ângelo"
          className="absolute right-0 top-0 h-full w-1/2 object-cover opacity-90 hidden lg:block"
        />
      </div>

      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 mt-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Text Content */}
          <div className="space-y-8 animate-fade-in">
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-gold/10 border border-gold/30">
              <span className="text-gold text-sm font-medium tracking-wider">REAL ESTATE</span>
            </div>
            
            <div className="space-y-6">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight heading-luxury">
                <span className="block text-white mb-2">
                  {t.hero.title}
                </span>
                <span className="text-gold block text-3xl md:text-4xl lg:text-4xl">
                  {t.hero.subtitle}
                </span>
              </h1>
              
              <div className="h-1 w-24 gold-gradient" />
              
              <p className="text-xl text-gray-300 leading-relaxed max-w-xl font-light">
                {t.hero.description}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => scrollToSection('team')} 
                size="lg" 
                className="bg-gradient-to-r from-gold to-gold-light hover:from-gold-light hover:to-gold text-black font-semibold group shadow-2xl px-8 py-6 text-lg"
                data-testid="hero-cta-team"
              >
                {t.hero.cta}
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                asChild
                size="lg" 
                variant="outline"
                className="border-2 border-gold text-gold hover:bg-gold hover:text-black group transition-all duration-300 px-8 py-6 text-lg"
                data-testid="hero-cta-phone"
              >
                <a href="tel:+351934608020">
                  <Phone className="mr-2 h-5 w-5 group-hover:rotate-12 transition-transform" />
                  {t.hero.contact}
                </a>
              </Button>
            </div>

            {/* Stats */}
            <div className="pt-8 border-t border-gold/20">
              <div className="space-y-2">
                <div className="text-5xl font-bold text-gold heading-luxury">8+</div>
                <div className="text-sm text-gray-400 uppercase tracking-wider">{t.about.experience}</div>
              </div>
            </div>
          </div>

          {/* Image Card for Mobile/Tablet */}
          <div className="relative lg:hidden">
            <div className="relative z-10">
              <div className="aspect-[3/4] rounded-2xl overflow-hidden luxury-shadow">
                <img 
                  src="https://customer-assets.emergentagent.com/job_realtyhub-19/artifacts/n3d0nlld_Foto-2.png"
                  alt="Miguel Ângelo"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              </div>
            </div>
            <div className="absolute -bottom-8 -right-8 w-72 h-72 bg-gradient-to-br from-gold/20 to-transparent rounded-full blur-3xl" />
          </div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent z-10" />
      <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-gold/5 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-gold/5 rounded-full blur-3xl" />
    </section>
  );
};

export default Hero;